import React, {FC, useEffect, useState} from 'react'
import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL

type Sub = {
  id: string
  status: string
  period?: 'monthly' | 'yearly'
  price?: number
  next_billing_at?: string
  payer_email?: string
}

type Status = {
  enabled: boolean
  prices?: {monthly: number; yearly: number}
  subscription?: Sub | null
}

const STATUS_LABEL: Record<string, string> = {
  APPROVAL_PENDING: 'ממתין לאישור ב-PayPal',
  APPROVED: 'אושר, ממתין לחיוב',
  ACTIVE: 'פעיל',
  SUSPENDED: 'מושהה (חיוב נכשל)',
  CANCELLED: 'בוטל',
  EXPIRED: 'הסתיים',
}

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString('he-IL') : '')

// PayPal subscription block. Renders nothing unless PayPal is turned on for this
// customer (Account.paypal_enabled, set by an admin). `compact` = buttons only,
// for the payment popup.
export const PaypalSubscription: FC<{compact?: boolean}> = ({compact}) => {
  const [status, setStatus] = useState<Status | null>(null)
  const [busy, setBusy] = useState<string>('')
  const [error, setError] = useState('')

  const load = () =>
    axios
      .get(`${API_URL}/api/paypal/status`)
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({enabled: false}))

  useEffect(() => {
    load()
  }, [])

  if (!status || !status.enabled) return null

  const sub = status.subscription
  const live = !!sub && ['ACTIVE', 'SUSPENDED', 'APPROVED'].includes(sub.status)

  const subscribe = async (period: 'monthly' | 'yearly') => {
    setError('')
    setBusy(period)
    try {
      const r = await axios.post(`${API_URL}/api/paypal/subscribe`, {period})
      // PayPal can't be shown in an iframe: send the whole page there and back.
      if (r.data?.url) {
        const target = window.top || window
        target.location.href = r.data.url
        return
      }
      setError('לא התקבל קישור מ-PayPal')
    } catch (e: any) {
      setError(e?.response?.data?.error || 'שגיאה ב-PayPal')
    }
    setBusy('')
  }

  const cancel = async () => {
    if (!window.confirm('לבטל את מנוי ה-PayPal? לא יבוצעו חיובים נוספים. התקופה שכבר שולמה נשארת בתוקף.')) return
    setError('')
    setBusy('cancel')
    try {
      await axios.post(`${API_URL}/api/paypal/cancel`)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.error || 'שגיאה בביטול')
    }
    setBusy('')
  }

  const btn: React.CSSProperties = {
    background: '#ffc439',
    color: '#003087',
    border: 'none',
    borderRadius: 8,
    padding: '10px 16px',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: 14,
  }

  if (live && sub) {
    if (compact) return null
    return (
      <div style={{marginTop: 16, padding: 14, border: '1px solid #e4e6ef', borderRadius: 10}}>
        <div style={{fontWeight: 700, marginBottom: 6}}>מנוי PayPal</div>
        <div style={{fontSize: 13.5, color: '#3f4254', lineHeight: 1.7}}>
          סטטוס: {STATUS_LABEL[sub.status] || sub.status}
          {sub.price ? <> · ₪{sub.price} {sub.period === 'yearly' ? 'לשנה' : 'לחודש'}</> : null}
          {sub.next_billing_at ? <> · החיוב הבא: {fmtDate(sub.next_billing_at)}</> : null}
          {sub.payer_email ? <> · {sub.payer_email}</> : null}
        </div>
        <button onClick={cancel} disabled={!!busy} style={{marginTop: 10, background: 'none', border: '1px solid #f1416c', color: '#f1416c', borderRadius: 8, padding: '6px 12px', cursor: 'pointer'}}>
          {busy === 'cancel' ? 'מבטל...' : 'ביטול מנוי PayPal'}
        </button>
        {error && <div style={{color: '#f1416c', marginTop: 8, fontSize: 13}}>{error}</div>}
      </div>
    )
  }

  const prices = status.prices || {monthly: 0, yearly: 0}
  return (
    <div style={{marginTop: compact ? 12 : 16, padding: compact ? 0 : 14, border: compact ? 'none' : '1px solid #e4e6ef', borderRadius: 10}}>
      {!compact && <div style={{fontWeight: 700, marginBottom: 8}}>תשלום במנוי PayPal</div>}
      <div style={{display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: compact ? 'center' : 'flex-start'}}>
        {prices.monthly > 0 && (
          <button style={btn} disabled={!!busy} onClick={() => subscribe('monthly')}>
            {busy === 'monthly' ? 'מעביר ל-PayPal...' : `PayPal · ₪${prices.monthly} לחודש`}
          </button>
        )}
        {prices.yearly > 0 && (
          <button style={btn} disabled={!!busy} onClick={() => subscribe('yearly')}>
            {busy === 'yearly' ? 'מעביר ל-PayPal...' : `PayPal · ₪${prices.yearly} לשנה`}
          </button>
        )}
      </div>
      {!compact && (
        <p style={{margin: '8px 2px 0', fontSize: 12.5, color: '#7e8299'}}>
          חיוב אוטומטי דרך PayPal בכל חודש או שנה. אפשר לבטל בכל רגע.
        </p>
      )}
      {sub && sub.status === 'CANCELLED' && !compact && (
        <div style={{fontSize: 12.5, color: '#7e8299', marginTop: 6}}>המנוי הקודם בוטל.</div>
      )}
      {error && <div style={{color: '#f1416c', marginTop: 8, fontSize: 13}}>{error}</div>}
    </div>
  )
}

export default PaypalSubscription
